import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { applyFilters } from "@/lib/filters";

import { humanDate, personName } from "./format";
import {
  cellKey, cellWrite, cleanNote, deleteAssignment, editableMap, EVAL_COPY, keptApart, mapDone, NO_STRATEGY, NOTE_MAX, noteWrite, overrideWrite, putAssignment,
  SAME_AS_LANE, setLaneNote, setProductNote, STRATEGIES_COPY as T, strategyColour, strategyValue, type EditCell, type EditLane, type EditProduct, type LaneNoteSlot, type MapSend,
  type MapWrite, type MarketplaceSlot, type Note, type StrategyEntry, type StrategyMap,
} from "./strategies";
import { SectionHead, StrategyDot, useStrategyWrite, type Editor } from "./StrategyParts";
import { filterMap, MAP_FILTER_COPY, mapFilterSpecs, mapFiltersFrom, UNASSIGNED_KEY } from "./strategy_filters";

/**
 * The product-channel map as the legacy sheet's dropdowns (Q6): products down, lanes across, one Select per strategised cell
 * (every strategy by name, and No strategy). An Amazon lane with several marketplaces opens to one Select per marketplace (the
 * lane's strategy, or a strategy of its own). The UAE lanes are not strategised: their cells stay inert with C2's sentence. Product
 * notes sit under the product, marketplace notes under the lane, the assignment's own note under its dropdown (review round 2), all
 * editable in place; a marketplace's lane note also shows, read-only, on its row under Marketplaces. A write names the cell's SKU
 * and key, as C2 takes it, and the strategy or note the tab saw there, so C2 refuses a write over a change made elsewhere.
 */
export function StrategyMapEditor({ maps, strategies, editor }: { maps: StrategyMap[]; strategies: StrategyEntry[]; editor: Editor }) {
  const all = editableMap(maps);
  // The Price Run's filter bar over the map (Damien, 31 Oct 2026), its state in the URL: the f_ keys carry across tabs.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const filters = mapFiltersFrom(maps, search);
  const onlyUnassigned = search[UNASSIGNED_KEY] === "1";
  const map = filterMap(all, maps, filters, onlyUnassigned);
  const inert = (lane: EditLane) => maps.find((m) => m.market === lane.market)?.not_strategised ?? undefined;
  return (
    <section className="flex flex-col gap-2" data-section="map">
      <SectionHead title={T.map.title} />
      <FilterBar layout="inline" filters={mapFilterSpecs(maps, filters)} values={filters} onChange={(next) => go(applyFilters(search, next))}
        afterFilters={(
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={onlyUnassigned} data-nonmutating onCheckedChange={(c) => go({ ...search, [UNASSIGNED_KEY]: c === true ? "1" : undefined })} />
            {MAP_FILTER_COPY.onlyUnassigned}
          </label>
        )}
        trailing={MAP_FILTER_COPY.showing(map.products.length, all.products.length)} />
      {map.products.length === 0 ? <p className="text-sm text-ks-grey-1">{MAP_FILTER_COPY.noMatch}</p> : (
      <Card><CardContent className="overflow-x-auto py-2">
        {/* Fixed layout with one width per lane: sized to content, a lane with a long note (Shopify EU and UK) grew to several
            times the width of Amazon EU or UAE (Bojan, 2026-10-07). The product column has its own width too: with none, six
            lanes wider than the card left it zero, and its header overprinted Amazon EU (Damien, 31 Oct 2026). */}
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-48 whitespace-normal py-2 align-top">{T.map.columns.product}</TableHead>
              {map.lanes.map((l) => <TableHead key={l.key} className="w-56 whitespace-normal py-2 align-top"><LaneHead lane={l} editor={editor} /></TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {map.products.map((p) => (
              <TableRow key={p.key}>
                <TableCell className="whitespace-normal align-top break-words"><ProductHead product={p} editor={editor} /></TableCell>
                {map.lanes.map((l) => (
                  <TableCell key={l.key} className="whitespace-normal align-top break-words">
                    <CellEditor cell={map.cells[cellKey(l.key, p.key)]} lane={l} product={p.key} strategies={strategies} editor={editor} inert={inert(l)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      )}
      {map.notStrategised.map((n) => <p key={n} className="text-sm text-ks-grey-1">{n}</p>)}
    </section>
  );
}

function ProductHead({ product, editor }: { product: EditProduct; editor: Editor }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-ks-ink">{product.key}</span>
      <NoteLine subject={`${T.map.productNote}, ${product.key}`} note={product.note} editor={editor}
        save={(text) => setProductNote({ tracking: product.tracking, variant: product.variant }, text, product.note?.text ?? null)} />
    </div>
  );
}

/** The lane's label and its marketplace notes (C2's lane notes, one per marketplace): shown under a one-marketplace lane, behind a
 * toggle on a lane of several. A lane that is not strategised carries none. */
export function LaneHead({ lane, editor, expanded = false }: { lane: EditLane; editor: Editor; expanded?: boolean }) {
  const [open, setOpen] = useState(expanded);
  const notes = lane.strategised ? lane.notes : [];
  const line = (n: LaneNoteSlot) => (
    <NoteLine key={n.marketplace_id} subject={T.map.laneNote(n.marketplace_name)} note={n.note} editor={editor} label={notes.length > 1 ? n.marketplace_name : undefined}
      save={(text) => setLaneNote({ channel_location: n.channel_location, marketplace_id: n.marketplace_id }, text, n.note?.text ?? null)} />
  );
  return (
    <div className="flex flex-col gap-1">
      <span>{lane.label}</span>
      {notes.length === 1 && line(notes[0])}
      {notes.length > 1 && (
        <>
          <Button size="xs" variant="ghost" className="w-fit px-0 text-ks-purple" data-nonmutating aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? T.map.hideMarketplaceNotes : T.map.marketplaceNotes}
          </Button>
          {open && notes.map(line)}
        </>
      )}
    </div>
  );
}

/** One cell: a dash for a lane with no SKU of the product, C2's sentence for a lane that is not strategised, else the Select. */
export function CellEditor({ cell, lane, product, strategies, editor, inert, expanded = false }: {
  cell: EditCell | undefined; lane: EditLane; product: string; strategies: StrategyEntry[]; editor: Editor; inert?: string; expanded?: boolean;
}) {
  const [open, setOpen] = useState(expanded);
  const write = useStrategyWrite(editor, (w: MapSend) =>
    w.write.kind === "put" ? putAssignment(w.write.sku, w.write.body) : deleteAssignment(w.write.sku, w.write.marketplace_id),
  (answer, w) => mapDone(product, w, answer));
  if (!cell) return <span className="text-sm text-ks-grey-1">{T.map.none}</span>;
  if (!cell.strategised) return <span className="text-sm text-ks-grey-1" title={inert}>{EVAL_COPY.notApplied}</span>;
  // the marketplaces that keep their own strategy, as the click saw them: a cleared lane's result line names them
  const send = (w: MapWrite | null, place: string, isLane: boolean) => { if (w) write.mutate({ write: w, place, lane: isLane, kept: isLane ? keptApart(cell) : undefined }); };
  const apart = cell.marketplaces.filter((m) => m.ownId !== null);
  // the assignment's own note (C2 `assignment.note`), saved through the same put with the strategy unchanged; none without an assignment
  const noteLine = (slot: MarketplaceSlot | null, note: string | null, place: string) => noteWrite(cell, slot, null) && (
    <NoteLine subject={T.map.cellNote(product, place)} note={note === null ? null : { text: note }} editor={editor}
      save={(text) => { const w = noteWrite(cell, slot, text) as NonNullable<ReturnType<typeof noteWrite>>; return putAssignment(w.sku, w.body); }} />
  );
  const sameAsLane = T.map.sameAsLane(lane.label);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <StrategyDot colour={strategyColour(strategies, cell.strategyName)} />
        <StrategySelect value={strategyValue(cell.strategyId, NO_STRATEGY)} shown={cell.strategyName ?? cell.notSet ?? T.map.noStrategy} none={{ value: NO_STRATEGY, label: T.map.noStrategy }}
          strategies={strategies} label={T.map.cellLabel(product, lane.label)} disabled={write.isPending} onChange={(v) => send(cellWrite(cell, v, strategies), lane.label, true)} />
      </div>
      {noteLine(null, cell.note, lane.label)}
      {apart.map((m) => <span key={m.marketplace_id} className="text-xs whitespace-normal text-ks-grey-1">{T.map.override(m.marketplace_name, m.ownName ?? "")}</span>)}
      {cell.marketplaces.length > 0 && (
        <Button size="xs" variant="ghost" className="w-fit px-0 text-ks-purple" data-nonmutating aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? T.map.hideMarketplaces : T.map.marketplaces}
        </Button>
      )}
      {open && cell.marketplaces.map((m: MarketplaceSlot) => (
        <div key={m.marketplace_id} className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="w-28 text-xs text-ks-grey-1">{m.marketplace_name}</span>
            <StrategyDot colour={strategyColour(strategies, m.ownName)} />
            <StrategySelect value={strategyValue(m.ownId, SAME_AS_LANE)} shown={m.ownName ?? sameAsLane} none={{ value: SAME_AS_LANE, label: sameAsLane }} first
              // a one-marketplace pool is set for the whole lane (C2 refuses `single_marketplace_pool`): a leftover own row can only be cleared
              strategies={cell.poolSingle ? [] : strategies} label={T.map.cellLabel(product, m.marketplace_name)} disabled={write.isPending} onChange={(v) => send(overrideWrite(cell, m, v, strategies), m.marketplace_name, false)} />
          </div>
          {/* the marketplace's lane note, read-only here; it is edited under the lane (Marketplace notes) */}
          {m.note && <span className="text-xs whitespace-normal text-ks-grey-1">{m.note.text}</span>}
          {noteLine(m, m.ownNote, m.marketplace_name)}
        </div>
      ))}
    </div>
  );
}

/** Every strategy by name plus the cell's "none" choice (No strategy on a lane, Same as lane on a marketplace). The value is
 * C2's id as a string and goes back to C2 by lookup (`cellWrite`, `overrideWrite`), never parsed. */
function StrategySelect({ value, shown, none, first, strategies, label, disabled, onChange }: {
  value: string; shown: string; none: { value: string; label: string }; first?: boolean; strategies: StrategyEntry[]; label: string; disabled: boolean; onChange: (value: string) => void;
}) {
  const noneItem = <SelectItem key={none.value} value={none.value}>{none.label}</SelectItem>;
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-44 *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:truncate" aria-label={label}><SelectValue>{shown}</SelectValue></SelectTrigger>
      <SelectContent>
        {first && noneItem}
        {strategies.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
        {!first && noneItem}
      </SelectContent>
    </Select>
  );
}

/** A note shown in place: a product, marketplace or assignment note (the last with no setter line: C2 stamps the assignment, not
 * the note), the text and who set it, then Add note or Edit note; editing, a box with Save note, Clear note (when one is set) and
 * Cancel. Blank saves as a clear, as C2 stores it (`cleanNote`); the result line says which was sent, or C2's "no change". */
export type NoteShown = Pick<Note, "text"> & Partial<Note>;
export function NoteLine({ subject, note, label, editor, save, open = false }: {
  subject: string; note: NoteShown | null; label?: string; editor: Editor; save: (text: string | null) => Promise<unknown>; open?: boolean;
}) {
  const [editing, setEditing] = useState(open);
  const [text, setText] = useState(note?.text ?? "");
  const write = useStrategyWrite(editor, save, (answer, sent) => noteDone(answer, sent));
  const submit = (value: string | null) => write.mutate(value, { onSuccess: () => setEditing(false) });
  const action = note ? T.map.editNote : T.map.addNote;
  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        {label && <span className="text-xs font-normal text-ks-grey-1">{label}</span>}
        <Textarea className="min-h-12 text-xs" maxLength={NOTE_MAX} aria-label={subject} placeholder={T.map.notePlaceholder} value={text} onChange={(e) => setText(e.target.value)} />
        <div className="flex flex-wrap gap-1">
          <Button size="xs" onClick={() => submit(text)} disabled={write.isPending}>{T.map.saveNote}</Button>
          {note && <Button size="xs" variant="outline" onClick={() => submit(null)} disabled={write.isPending}>{T.map.clearNote}</Button>}
          <Button size="xs" variant="outline" data-nonmutating onClick={() => { setText(note?.text ?? ""); setEditing(false); }}>{T.map.cancel}</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-0.5 text-xs font-normal">
      {note && <span className="text-ks-ink">{label ? `${label}: ` : ""}{note.text}</span>}
      {note?.set_by && note.set_at && <span className="text-ks-grey-1">{T.map.noteBy(personName(note.set_by), humanDate(note.set_at))}</span>}
      {!note && label && <span className="text-ks-grey-1">{label}</span>}
      <Button size="xs" variant="ghost" className="h-auto px-0 text-ks-purple" aria-label={T.map.noteAction(action, subject)} onClick={() => { setText(note?.text ?? ""); setEditing(true); }}>{action}</Button>
    </div>
  );
}
/** The result line of a note write: C2's "no change" when it says so, else cleared or saved by what was sent. */
export function noteDone(answer: unknown, sent: string | null): string {
  if (typeof answer === "object" && answer !== null && "unchanged" in answer && (answer as { unchanged?: boolean }).unchanged) return T.done.unchanged;
  return cleanNote(sent) === null ? T.done.noteCleared : T.done.noteSaved;
}
